/**
 * installPromptState — shared snooze + lifetime-cap state for the PWA install
 * coaching cards (Android beforeinstallprompt + iOS Add-to-Home-Screen).
 *
 * Persisted as a single JSON blob in localStorage so both prompts honor the
 * same cooldown and the same lifetime cap. Migrates the two legacy boolean
 * keys (`pwa-install-prompt-dismissed-v1`, `ios-install-hint-dismissed-v1`)
 * into a permanent dismiss so already-dismissed users are never re-prompted.
 */

const STATE_KEY = "gd_install_prompt_state_v1";
const LEGACY_ANDROID = "pwa-install-prompt-dismissed-v1";
const LEGACY_IOS = "ios-install-hint-dismissed-v1";

export const SNOOZE_DAYS = 7;
export const LIFETIME_CAP = 4;
/** First N sessions, we wait for an "install moment" event before showing.
 * After this many sessions the card is allowed to surface on dashboard load. */
export const SESSIONS_BEFORE_PASSIVE = 3;

export interface InstallPromptState {
  snoozeUntil: number | null;     // epoch ms
  shownCount: number;             // lifetime
  permanentlyDismissed: boolean;
  rewardedAt: number | null;      // epoch ms; once set, never prompt again
  sessionCount: number;
  lastSessionAt: number | null;
}

const DEFAULT_STATE: InstallPromptState = {
  snoozeUntil: null,
  shownCount: 0,
  permanentlyDismissed: false,
  rewardedAt: null,
  sessionCount: 0,
  lastSessionAt: null,
};

function safeParse(raw: string | null): InstallPromptState {
  if (!raw) return { ...DEFAULT_STATE };
  try {
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function migrateLegacy(): Partial<InstallPromptState> | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const a = localStorage.getItem(LEGACY_ANDROID);
    const i = localStorage.getItem(LEGACY_IOS);
    if (a === "1" || i === "1") {
      // Honor prior dismiss as a permanent dismiss so we don't surprise users.
      try { localStorage.removeItem(LEGACY_ANDROID); } catch {/* noop */}
      try { localStorage.removeItem(LEGACY_IOS); } catch {/* noop */}
      return { permanentlyDismissed: true };
    }
  } catch {/* noop */}
  return null;
}

export function readInstallPromptState(): InstallPromptState {
  if (typeof localStorage === "undefined") return { ...DEFAULT_STATE };
  let raw: string | null = null;
  try { raw = localStorage.getItem(STATE_KEY); } catch {/* noop */}
  const state = safeParse(raw);

  // Only migrate when we don't already have a state object.
  if (raw === null) {
    const legacy = migrateLegacy();
    if (legacy) {
      const merged = { ...state, ...legacy };
      writeInstallPromptState(merged);
      return merged;
    }
  }
  return state;
}

export function writeInstallPromptState(next: InstallPromptState): void {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem(STATE_KEY, JSON.stringify(next)); } catch {/* noop */}
}

function update(mut: (s: InstallPromptState) => InstallPromptState): InstallPromptState {
  const next = mut(readInstallPromptState());
  writeInstallPromptState(next);
  return next;
}

export function snoozeInstallPrompt(days = SNOOZE_DAYS): InstallPromptState {
  return update((s) => ({
    ...s,
    snoozeUntil: Date.now() + days * 24 * 60 * 60 * 1000,
    shownCount: s.shownCount + 1,
  }));
}

export function permanentlyDismissInstallPrompt(): InstallPromptState {
  return update((s) => ({ ...s, permanentlyDismissed: true }));
}

export function markInstallRewarded(): InstallPromptState {
  return update((s) => ({ ...s, rewardedAt: Date.now(), permanentlyDismissed: true }));
}

/** Increment session counter once per page load, throttled to once every 30 min. */
export function bumpSession(): InstallPromptState {
  return update((s) => {
    const now = Date.now();
    if (s.lastSessionAt && now - s.lastSessionAt < 30 * 60 * 1000) return s;
    return { ...s, sessionCount: s.sessionCount + 1, lastSessionAt: now };
  });
}

export interface ShouldShowOptions {
  /** True when an explicit "install moment" event has fired (goal completed,
   * certificate generated, streak milestone). Bypasses the passive-session
   * threshold. */
  armed?: boolean;
}

export function isEligibleToPrompt(opts: ShouldShowOptions = {}): boolean {
  const s = readInstallPromptState();
  if (s.permanentlyDismissed) return false;
  if (s.rewardedAt) return false;
  if (s.shownCount >= LIFETIME_CAP) return false;
  if (s.snoozeUntil && s.snoozeUntil > Date.now()) return false;
  if (!opts.armed && s.sessionCount < SESSIONS_BEFORE_PASSIVE) return false;
  return true;
}
